import { Edit, PlayCircle, Trash } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { RootState } from "../state/store";
import { useSelector } from "react-redux";
import axios from "axios";
import { Card } from "./ui/card";
import { current } from "@reduxjs/toolkit";
import { BroadcastModal } from "./ui/agents/broadcastmodal";
import Swal from "sweetalert2";
type EventsProps = {
    currentUser: any,
    onLogout: any,
    baseUrl: string,
    onLoginClick: any,
    onLoginSuccess: any,
    selectedEvent: any,
    setSelectedEvent: (e: any) => void,
}
export default function Events({ currentUser, onLogout, baseUrl, onLoginClick, onLoginSuccess, selectedEvent, setSelectedEvent }: EventsProps) {

    const [view, setView] = useState(currentUser?.role === "support" || currentUser?.role === "client" ? "list" : "grid");

    const client = useSelector((state: RootState) => state.client.client);

    const [events, setEvents] = useState([]);

    const [wallet, setWallet] = useState(0);
    const [scrolled, setScrolled] = useState(false);

    const path = window.location.pathname;

    const redirect = path === "/streaming-now" ? window.location.origin + "/upcoming-events" : window.location.origin + "/streaming-now"

    const getEvents = () => {
        let apiPath = "";
        if (path === "/streaming-now") {
            apiPath = baseUrl + "/getStreamingNow";
        } else {
            apiPath = baseUrl + "/getUpcomingEvents";
        }
        axios.get(apiPath, { params: { client_uid: client?.uid } }).then((response) => {
            // handle response
            if (response.data.status === "OK") {
                setEvents(response.data.data.events);
            }
        });
    };
    useEffect(() => {
        getEvents();
    }, [baseUrl, client])

    useEffect(() => {
        const channel = new BroadcastChannel("CHAMCORE-BROADCAST-ADD-ON-BEHALF-OF-AGENT");
        channel.onmessage = (event) => {
          if (event.data?.type === "BROADCAST_ADDED") {
            getEvents();
          }
        };
        return () => channel.close();
      }, []);

    const [showBroadcastModal, setShowBroadcastModal] = useState(false);

    const handleCancelClick = async (id: string) => {
        const result = await Swal.fire({
            title: "Kanseller konsultasjon",
            html: `
                            <h3 style="margin-bottom: 2rem !important">Er du sikker på at du vil avbestille denne konsultasjonen?</h3>
                            <div style="text-align: left; margin: 0 0 16px; padding: 12px 14px; border-radius: 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-size: 14px; color: #111827;">
                                <strong>Merk:</strong> Når en agent kansellerer en konsultasjon, blir hele det autoriserte beløpet frigitt tilbake til kunden umiddelbart.
                            </div>
                        `,
            showCancelButton: true,
            confirmButtonText: "Kanseller konsultasjon",
            cancelButtonText: "Close",
            customClass: {
                confirmButton: "swal-confirm-btn",
                cancelButton: "swal-cancel-btn",
            },
        });
        if (result.isConfirmed) {
            try {
                let formData = new FormData();
                formData.append("token", currentUser.token);
                formData.append("id", id);
                const response = await axios.post(baseUrl + '/deleteBroadcast', formData);
                if (response.data.status === "OK") {
                    getEvents();
                    Swal.fire({
                        title: 'Success',
                        text: response.data.data,
                        icon: 'success',
                        timer: 1500,
                    });
                } else {
                    Swal.fire({
                        title: response.data.status,
                        text: response.data.data,
                        icon: 'error',
                        showConfirmButton: true,
                    })
                }
            } catch (err) {
                console.error(err);
            }
        }
    }

    return (
        <div className="w-full min-h-screen bg-gradient-to-b from-white to-slate-50 py-16 px-6 animate-fadeIn">
            {/* Top Navigation */}
            <div className="max-w-6xl mx-auto flex flex-wrap gap-4 justify-between items-center mb-10">
                <h1 className="text-3xl font-bold text-slate-900">{currentUser?.role !== "support" && currentUser?.role !== "clients" ? path === "/streaming-now" ? "Streaming nå" : "Kommende eventer" : ''}</h1>


                <div className="flex items-center flex-wrap gap-4">
                    {/* View Toggle */}
                    <div className="flex items-center gap-3 bg-white/70 backdrop-blur-xl px-3 py-2 rounded-full border border-white/40 shadow-sm">
                        <button
                            onClick={() => setView("grid")}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition ${view === "grid"
                                ? "bg-purple-600 text-white"
                                : "text-slate-600 hover:text-slate-800"
                                }`}
                        >
                            Grid
                        </button>
                        <button
                            onClick={() => setView("list")}
                            className={`px-4 py-1 rounded-full text-sm font-medium transition ${view === "list"
                                ? "bg-purple-600 text-white"
                                : "text-slate-600 hover:text-slate-800"
                                }`}
                        >
                            List
                        </button>
                    </div>

                    {/* Links */}
                    {currentUser?.role !== "support" && currentUser?.role !== "client" ? (
                        <div className="flex gap-4 text-sm">
                            <a
                                href={redirect}
                                className="text-blue-600 hover:text-blue-800 transition font-medium"
                            >
                                {path === "/streaming-now" ? "Kommende eventer" : "Streaming nå"}
                            </a>
                            <a
                                href="/agents"
                                className="text-blue-600 hover:text-blue-800 transition font-medium"
                            >
                                Våre veiledere
                            </a>
                        </div>
                    ) : ('')}

                </div>
            </div>

            {/* Event List/Grid */}
            <div
                className={`${view === "grid"
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-10"
                    : "flex flex-col gap-6"
                    } max-w-6xl mx-auto`}
            >
                {events?.length < 1 && (
                    <div>No {path === "/streaming-now" ? "streaming " : "upcoming "} events</div>
                )}
                {events.map((e) => (
                    <Card
                        key={e.id}
                        onClick={() => setSelectedEvent(e)}
                        className={`rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl bg-white/80 backdrop-blur-xl border border-white/20 transition-all duration-500 hover:-translate-y-2 ${view === "list" ? "flex h-52" : ""
                            }`}
                    >
                        {/* Image */}
                        <div
                            className={`relative overflow-hidden ${view === "list" ? "w-1/3" : "w-full h-64"
                                }`}
                        >
                            <img
                                src={e.uploaded_file}
                                alt={e.title}
                                className="w-full h-full object-cover transition duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 via-fuchsia-600/30 to-purple-700/40" />
                            {e.status === "live" && path !== "/upcoming-events" && (
                                <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md animate-pulse">
                                    LIVE
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className={`${view === "list" ? "w-2/3 p-6" : "p-6"}`}>
                            <h2 className="text-xl font-bold text-slate-900 mb-2">
                                {e.title}
                            </h2>
                            <p className="text-slate-700 text-sm mb-4">{e.description}</p>

                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-purple-700 font-semibold">
                                        {e.broadcast_price === 0 ? "Gratis" : `${e.broadcast_price} kr`}
                                    </span>
                                </div>
                                <div>
                                    {currentUser?.role === "support" || currentUser?.role === "clients" ? (
                                        <>
                                            <button onClick={() => handleCancelClick(e.id)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition">
                                                <Trash className="w-4 h-4" />
                                                <span className="md:flex hidden">Slett</span>
                                            </button>
                                            <button onClick={() => setShowBroadcastModal(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition">
                                                <Edit className="w-4 h-4" />
                                                <span className="md:flex hidden">Redigere</span>
                                            </button>
                                        </>
                                    ) : (
                                        <button className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition">
                                            <PlayCircle className="w-4 h-4" />
                                            {e.broadcast_price === 0 ? "Delta gratis" : "Kjøp tilgang"}
                                        </button>
                                    )}

                                </div>

                            </div>
                        </div>
                    </Card>
                ))}
            </div>
            {/* Only render BroadcastModal for support or client roles */}
            {["support", "client"].includes(currentUser?.role) && client && currentUser && (
                <BroadcastModal
                    show={showBroadcastModal}
                    onClose={() => setShowBroadcastModal(false)}
                    baseUrl={baseUrl}
                    currentUser={currentUser}
                />
            )}
        </div >
    )
}